import { useState, useCallback, useEffect } from 'react'

export interface DialogState<TData extends Record<string, any>> {
  isOpen: boolean
  data: TData | null
  isLoading: boolean
  error: string | null
  onConfirm: (() => void | Promise<void>) | null
}

export interface UseDialogOptions {
  closeOnEscape?: boolean
  onSuccess?: (data: any) => void
  onError?: (error: string) => void
}

export interface UseDialogReturn<TData extends Record<string, any>> {
  state: DialogState<TData>
  open: (data: TData, onConfirm?: () => void | Promise<void>) => void
  close: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  updateData: (data: Partial<TData>) => void
}

/**
 * Generic dialog hook - type-safe state for any dialog pattern
 * BUG FIX: ESC handler included (was missing in useConfirmationDialog)
 */
export const useDialog = <TData extends Record<string, any>>(
  options: UseDialogOptions = {}
): UseDialogReturn<TData> => {
  const { closeOnEscape = true, onSuccess, onError } = options

  const [state, setState] = useState<DialogState<TData>>({
    isOpen: false,
    data: null,
    isLoading: false,
    error: null,
    onConfirm: null,
  })

  const open = useCallback(
    (data: TData, onConfirm?: () => void | Promise<void>) => {
      setState({
        isOpen: true,
        data,
        isLoading: false,
        error: null,
        onConfirm: onConfirm || null,
      })
    },
    []
  )

  const close = useCallback(() => {
    setState({
      isOpen: false,
      data: null,
      isLoading: false,
      error: null,
      onConfirm: null,
    })
  }, [])

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({
      ...prev,
      isLoading: loading,
      error: loading ? null : prev.error,
    }))
  }, [])

  const setError = useCallback(
    (error: string | null) => {
      setState(prev => ({ ...prev, error, isLoading: false }))
      if (error && onError) onError(error)
    },
    [onError]
  )

  const updateData = useCallback((partialData: Partial<TData>) => {
    setState(prev => ({
      ...prev,
      data: prev.data
        ? { ...prev.data, ...partialData }
        : (partialData as TData),
    }))
  }, [])

  // ESC key handler (BUG FIX!)
  useEffect(() => {
    if (!closeOnEscape || !state.isOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !state.isLoading) close()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [closeOnEscape, state.isOpen, state.isLoading, close])

  return { state, open, close, setLoading, setError, updateData }
}
